import React, {useState, useEffect, useContext} from 'react';
import {Form, Radio, DatePicker, Select, Divider, Input} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import GenericDialog from '@common/GenericDialog';
import dayjs from 'dayjs';
import axios from "@common/axios";
import SyllabusTopicConceptSelect from "@common/SyllabusTopicConceptSelect";
import Button from "@common/Button";
import MathJaxHTMLContent from "@common/MathJaxHTMLContent";
import RecipientSelect from "@common/RecipientSelect";
import CircularLoader from "@common/CircularLoader";
import {UserContext} from "@context/UserContext";
import {BrandContext} from "@context/BrandContext";

const AssignTrailDialog = props => {
    const {open, onClose, setReload, selectedTrail, assignFromCreateTrail = false} = props;
    const {user} = useContext(UserContext);
    const brandConfig = useContext(BrandContext);

    const CONTENT_OBJ = {
        SINGLE_CONCEPT: {name: brandConfig.conceptName, val: 'SINGLE_CONCEPT', inputLabel: brandConfig.conceptName, inputName: 'conceptIds', selectLevel: 'concept'},
        MULTIPLE_CONCEPT: {name: 'Multiple ' + brandConfig.conceptsName, val: 'MULTIPLE_CONCEPT', inputLabel: brandConfig.conceptsName, inputName: 'conceptIds', selectLevel: 'concept'},
        SINGLE_TOPIC: {name: brandConfig.topicName, val: 'SINGLE_TOPIC', inputLabel: brandConfig.topicName, inputName: 'topicId', selectLevel: 'topic'}
    }
    const CONTENT_ARR = [CONTENT_OBJ.SINGLE_CONCEPT, CONTENT_OBJ.MULTIPLE_CONCEPT, CONTENT_OBJ.SINGLE_TOPIC];
    const MAX_SELECTED_CONCEPTS = 10;

    const isConceptSpecific = selectedTrail?.conceptSpecific;
    const [assignTrailForm] = Form.useForm();
    const [contentType, setContentType] = useState(CONTENT_OBJ.SINGLE_CONCEPT);
    const [trailConcepts, setTrailConcepts] = useState({});
    const [trailTopics, setTrailTopics] = useState({});
    const [selectedConcepts, setSelectedConcepts] = useState([]);
    const [selectedConcept, setSelectedConcept] = useState(null);
    const [selectedConceptIds, setSelectedConceptIds] = useState([]);
    const [startDate, setStartDate] = useState(dayjs());
    const [recipientObj, setRecipientObj] = useState();
    const [classes, setClasses] = useState([]);

    useEffect(() => {
        if (open) {
            axios.get('/web/ui/myClasses/viewMyClasses')
                .then(response => {
                    setClasses(response.data.filter(clazz => clazz.totalStudents > 0));
                });
            setStartDate(dayjs());
            assignTrailForm.setFieldsValue({
                startDate: dayjs(),
                trailAssignmentName: selectedTrail?.pathwayName,
                contentType: CONTENT_OBJ.SINGLE_CONCEPT.val
            });
            if (isConceptSpecific && (!trailConcepts.hasOwnProperty(selectedTrail?.pathwayId) || !trailTopics.hasOwnProperty(selectedTrail?.pathwayId))) {
                let tempTrailConcepts,
                    tempTrailTopics;
                axios.get('/web/ui/trail/trailConcepts/' + selectedTrail?.pathwayId)
                    .then((response) => {
                        if (response.data) {
                            tempTrailConcepts = {[selectedTrail.pathwayId]: response.data};
                        }

                        return axios.get('/web/ui/trail/trailTopics/' + selectedTrail?.pathwayId)
                    })
                    .then((response) => {
                        if (response.data) {
                            tempTrailTopics = {[selectedTrail.pathwayId]: response.data};
                        }
                    })
                    .finally(() => {
                        if (tempTrailConcepts) setTrailConcepts(tempTrailConcepts);
                        if (tempTrailTopics) setTrailTopics(tempTrailTopics);
                    })
                    .catch((error) => {
                        console.error('Error loading concepts: ',error);
                    });
            }
        } else {
            setStartDate(null);
            setSelectedConcepts([]);
            setSelectedConcept(null);
            setSelectedConceptIds([]);
            assignTrailForm.resetFields();
        }
    }, [open]);

    useEffect(() => {
        setSelectedConceptIds(selectedConcepts.map(concept => concept.conceptId));
    }, [selectedConcepts]);

    const handleAddConcept = () => {
        const tempConcepts = [
            ...(selectedConcept ? [selectedConcept] : []),
            ...selectedConcepts
        ];
        const tempConceptIds = tempConcepts.map(concept => concept.conceptId);

        setSelectedConcepts(tempConcepts);
        setSelectedConceptIds(tempConceptIds);
        assignTrailForm.setFieldValue('conceptIds', tempConceptIds);
        assignTrailForm.validateFields(['conceptIds']);
    }

    const onConceptChange = newConcept => {
        setSelectedConcept(newConcept);
    }

    const onSelectedConceptsChange = newConcepts => {
        setSelectedConcepts(selectedConcepts.filter(concept => newConcepts.includes(concept.conceptId)));
    }

    const onContentChange = ({target: {value}}) => {
        setContentType(CONTENT_OBJ[value]);
    }

    const assignTrail = async () => {
        assignTrailForm.validateFields()
            .then(values => {
                const params = {trailId: selectedTrail.pathwayId, assignFromCreateTrail: assignFromCreateTrail, ...values};

                params.startDate = params.startDate.format(user.getDateTimeFormatString());

                if (params.contentType === CONTENT_OBJ.SINGLE_CONCEPT.val) {
                    params.conceptIds = [params.conceptId];
                }
                if (params.contentType !== CONTENT_OBJ.SINGLE_TOPIC.val) {
                    delete params.topicId;
                }

                params.recipientGroup = recipientObj?.recipientGroup?.val;

                delete params.syllabusGroupId;
                delete params.syllabusId;
                delete params.conceptId;
                delete params.recipient;
                delete params.contentType;

                axios.post('/web/ui/trail/assignTrail', params, {headers: {'Content-Type': 'application/x-www-form-urlencoded'}})
                    .then(response => {
                        if (response.data.success) {
                            displayToastMessage(brandConfig.taskName + ' assigned successfully', null, 'success', undefined, 10000);

                            assignTrailForm.resetFields();
                            if (typeof setReload === 'function') setReload();
                            if (typeof onClose === 'function') onClose();
                        } else {
                            displayToastMessage('Error assigning test', response.data.message, 'error', undefined, 10000);
                        }
                    }).catch(error => {
                        console.error(error);
                    });
            });
    }

    return (
        <GenericDialog
            title={'Assign ' + brandConfig.taskName}
            open={open}
            contentStyle={{width: 630}}
            onClose={onClose}
            buttons={[
                {
                    name: 'Cancel',
                    extraProps: {type: 'text'},
                },
                {
                    name: 'Assign',
                    callback: assignTrail,
                }
            ]}
        >
            <Form
                className={'ch-form'}
                form={assignTrailForm}
                labelCol={{span: 6}}
                wrapperCol={{span: 18}}
            >
                <h2>{selectedTrail?.pathwayName}</h2>
                {isConceptSpecific &&
                    <>
                        <div>This {brandConfig.taskName} contains content from:</div>
                        {!trailConcepts.hasOwnProperty(selectedTrail?.pathwayId) &&
                            <CircularLoader pageCentered={true} displayInElement={true} spacing={false} />
                        }
                        {trailConcepts.hasOwnProperty(selectedTrail?.pathwayId) &&
                            <ul className={'ch-ellipsis-list'} style={{maxHeight: '150px'}}>
                                {trailConcepts[selectedTrail?.pathwayId].map(concept => {
                                    return <li key={concept.conceptId}>{concept.conceptName} ({brandConfig.conceptName.toLowerCase()})</li>
                                })}
                                {trailTopics[selectedTrail?.pathwayId].map(topic => {
                                    return <li key={topic.topicId}>{topic.topicName} ({brandConfig.topicName.toLowerCase()})</li>
                                })}
                            </ul>
                        }
                    </>
                }
                {!isConceptSpecific &&
                    <>
                        <Form.Item label={'Content'} name={'contentType'}>
                            <Radio.Group
                                optionType={'button'}
                                onChange={onContentChange}
                                options={CONTENT_ARR.map(contentType => {
                                    return {label: contentType.name, value: contentType.val}
                                })}
                            />
                        </Form.Item>
                        <SyllabusTopicConceptSelect
                            form={assignTrailForm}
                            selectLevel={contentType.selectLevel}
                            onChange={onConceptChange}
                            onLoadComplete={onConceptChange}
                        />
                        {contentType.val === CONTENT_OBJ.MULTIPLE_CONCEPT.val &&
                            <>
                                <Form.Item label={'Selected ' + brandConfig.conceptsName} name={'conceptIds'}
                                           rules={[
                                               {
                                                   validator: () => {
                                                       if (selectedConceptIds.length === 0) {
                                                           return Promise.reject('Please select at least one ' + brandConfig.conceptName + '.');
                                                       }
                                                       return Promise.resolve();
                                                   },
                                               },
                                           ]}
                                >
                                    <div className={'flexColumn'}>
                                        <div className={'flexAlignCentre'}>
                                            <Select
                                                virtual={false}
                                                showSearch={false}
                                                allowClear
                                                autoClearSearchValue
                                                mode={'multiple'}
                                                maxTagCount={'responsive'}
                                                value={selectedConceptIds}
                                                onChange={onSelectedConceptsChange}
                                                notFoundContent={'No ' + brandConfig.conceptsName + ' selected'}
                                                style={{minWidth: 0}}
                                            >
                                                {selectedConcepts.map(concept => {
                                                    return (
                                                        <Select.Option key={concept.conceptId + '-s'} value={concept.conceptId}>
                                                            <MathJaxHTMLContent html={concept.conceptName} style={{overflow:'hidden', textOverflow:'ellipsis'}} />
                                                        </Select.Option>
                                                    );
                                                })}
                                            </Select>
                                            <Button htmlType={"button"} className={'margin-l--m'} aria-label={'Add ' + brandConfig.conceptsName}
                                                    disabled={selectedConcepts.length === MAX_SELECTED_CONCEPTS || selectedConceptIds.includes(selectedConcept?.conceptId)}
                                                    onClick={handleAddConcept}>Add {brandConfig.conceptName}</Button>
                                        </div>
                                        <span className={'ch-text-emphasis margin-t--m padding-l--s'}>{selectedConcepts.length} of {MAX_SELECTED_CONCEPTS} {brandConfig.conceptsName} selected</span>
                                    </div>
                                </Form.Item>
                            </>
                        }
                    </>
                }
                <Divider className={'margin--rm'} />
                <Form.Item
                    label={'Start date'}
                    name={'startDate'}
                    rules={[
                        {
                            required: true,
                            message: 'Please select a start date.',
                        },
                        {
                            validator: (_, value) => {
                                if (startDate && value && value < startDate) {
                                    return Promise.reject(`Start date must not be before (${startDate.format(user.getDateFormatString() + ', HH:mm')}).`);
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <DatePicker
                        showTime={{
                            format: 'HH:mm',
                        }}
                        format={user.getDateTimeFormatString()}
                        placement={'topLeft'}
                        style={{width: '100%'}}
                    />
                </Form.Item>
                <Form.Item label={brandConfig.taskName + ' name'} name={'trailAssignmentName'} rules={[{ required: true, message: `Please enter a ${brandConfig.taskName.toLowerCase()} name.` }]}>
                    <Input />
                </Form.Item>
                <Form.Item label={'Message for students'} name={'message'}>
                    <TextArea rows={4}/>
                </Form.Item>
                <RecipientSelect form={assignTrailForm} classes={classes} setRecipientObj={setRecipientObj} />
            </Form>
        </GenericDialog>
    );
};

export default AssignTrailDialog;
